import {PIXI} from '@colonist/pixi'
import {AsyncAssetLoader} from './AsyncAssetLoader'
import {EventDiceEnum} from '@colonist/game-base-types'
import {logError} from './ui_utils/Utils'
import {Sprites, getImageNameFromFileName, getImagesToPreload} from './ui_game/UIGameImageDictionary'
import {ViewPopupAssetsLoading} from './ui_popup/ViewPopupAssetsLoading'
import {UIImagePathController} from './UIEnums.ts'
import {AbortError, ImageURL, LobbyImage} from '@colonist/types'
import {PlayerValidator, TileCorner} from '@colonist/game-mechanic-validators'
import {ArrayUtils, createDeferredPromise, EnumUtils} from '@colonist/utils'
import {uiGameManager} from './EntryShared'
import {CityImprovementTypes, PlayerColor, TileCornerBonusType, TileCornerBuildingType, TileEdgeDirection, TileEdgePortType, TileHexType} from '@colonist/game-states'

export module UIGameSpriteHandler {

    let loadingPopup: ViewPopupAssetsLoading
    let spritesheetMap: Map<string, string> | undefined
    let loadedAssets: Record<string, PIXI.Spritesheet | PIXI.Texture> | undefined

    const baseBundleName: string = 'game'

    let bundleManifest: PIXI.AssetsManifest | undefined

    export async function initializeBundle(): Promise<void> {
        if(bundleManifest != undefined) return
        const assetsArray: PIXI.UnresolvedAsset = []

        for(const img of getImagesToPreload()) {
            assetsArray.push({name: img.name, srcs: [{src: img.path}]})
        }

        bundleManifest = {
            bundles: [
                {
                    name: baseBundleName,
                    assets: assetsArray,
                },
            ],
        }
        await PIXI.Assets.init({manifest: bundleManifest})
    }

    export async function loadImages() {
        await UIGameSpriteHandler.initializeBundle()
        void PIXI.Assets.backgroundLoadBundle(baseBundleName)
    }

    export async function continueAfterAssetsLoad(action: () => void = () => undefined, xAction: () => void = () => undefined): Promise<void> {
        if(loadedAssets != undefined) {
            action()
            return
        }

        const promise = createDeferredPromise()

        const resolvePromiseAndRunAction = () => {
            action()
            promise.resolve()
        }

        if(loadingPopup != undefined && !loadingPopup.isRemoved()) {
            loadingPopup.addCallback(resolvePromiseAndRunAction)
            return promise
        }
        loadingPopup = new ViewPopupAssetsLoading(resolvePromiseAndRunAction, () => {
            xAction()
            promise.reject(new AbortError())
        })
        loadingPopup.show()

        void startLoadingAssets()

        return promise
    }

    export async function startLoadingAssets() {
        await UIGameSpriteHandler.initializeBundle()
        loadedAssets = await PIXI.Assets.loadBundle(baseBundleName, (progress) => {
            loadingPopup.updateLoadPercentage(progress * 100)
        })

        loadingPopup.finishedLoading()
    }

    export function getProbabilityImage(prob: number): PIXI.Texture | undefined {
        const allowedDiceNumbers = ['2', '3', '4', '5', '6', '8', '9', '10', '11', '12']
        if(!allowedDiceNumbers.includes(prob.toString())) return undefined

        const validDiceNumberStringTypes = ['2', '3', '4', '5', '6', '8', '9', '10', '11', '12'] as const
        const probString = prob.toString() as typeof validDiceNumberStringTypes[number]
        const textureKey: Sprites = Sprites[`prob_${probString}`]
        return getTextureFromSpritesheet(textureKey)
    }

    export function getDiceImage(num: number, redDice?: boolean): PIXI.Texture {
        const allowedDiceNumbers = ['1', '2', '3', '4', '5', '6']
        if(!allowedDiceNumbers.includes(num.toString())) num = 1

        const redText = redDice ? '_red' : ''
        const validDiceNumberStringTypes = ['1', '2', '3', '4', '5', '6'] as const
        const numberString = num.toString() as typeof validDiceNumberStringTypes[number]
        const diceTextureKey: Sprites = Sprites[`dice${redText}_${numberString}`]
        return getTextureFromSpritesheet(diceTextureKey)
    }

    export function getHexImage(type: TileHexType): PIXI.Texture {
        switch(type) {
            case TileHexType.Lumber:
                return getTextureFromSpritesheet(Sprites.tile_lumber)
            case TileHexType.Brick:
                return getTextureFromSpritesheet(Sprites.tile_brick)
            case TileHexType.Wool:
                return getTextureFromSpritesheet(Sprites.tile_wool)
            case TileHexType.Desert:
                return getTextureFromSpritesheet(Sprites.tile_desert)
            case TileHexType.Grain:
                return getTextureFromSpritesheet(Sprites.tile_grain)
            case TileHexType.Ore:
                return getTextureFromSpritesheet(Sprites.tile_ore)
            case TileHexType.Gold:
                return getTextureFromSpritesheet(Sprites.tile_gold)
            case TileHexType.Fog:
                return getTextureFromSpritesheet(Sprites.tile_fog)
            case TileHexType.Sea:
                return getTextureFromSpritesheet(Sprites.tile_sea)
        }
    }

    export function getCornerImage(corner: TileCorner): PIXI.Texture | undefined {
        if(corner.owner == undefined) return undefined
        if(corner.buildingType == undefined) return undefined
        switch(corner.buildingType) {
            case TileCornerBuildingType.Settlement:
                return getSettlementForPlayer(corner.owner)
            case TileCornerBuildingType.City:
                return getCityForPlayer(corner.owner)
            case TileCornerBuildingType.DestroyedCity:
                return getDestroyedCityForPlayer(corner.owner)
            case TileCornerBuildingType.KnightActiveLevel1:
            case TileCornerBuildingType.KnightActiveLevel2:
            case TileCornerBuildingType.KnightActiveLevel3:
            case TileCornerBuildingType.KnightInactiveLevel1:
            case TileCornerBuildingType.KnightInactiveLevel2:
            case TileCornerBuildingType.KnightInactiveLevel3:
                return getKnightForPlayer(corner.owner, corner.buildingType)
        }
    }

    export function getPortImage(portType: TileEdgePortType): PIXI.Texture | undefined {
        switch(portType) {
            case TileEdgePortType.Port:
                return getTextureFromSpritesheet(Sprites.port)
            case TileEdgePortType.PortLumber:
                return getTextureFromSpritesheet(Sprites.port_lumber)
            case TileEdgePortType.PortBrick:
                return getTextureFromSpritesheet(Sprites.port_brick)
            case TileEdgePortType.PortWool:
                return getTextureFromSpritesheet(Sprites.port_wool)
            case TileEdgePortType.PortGrain:
                return getTextureFromSpritesheet(Sprites.port_grain)
            case TileEdgePortType.PortOre:
                return getTextureFromSpritesheet(Sprites.port_ore)
        }
    }

    export function getSettlementForPlayer(playerColor: PlayerColor): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`settlement_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getCityForPlayer(playerColor: PlayerColor): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`city_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getDestroyedCityForPlayer(playerColor: PlayerColor): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`city_destroyed_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getCityWallForPlayer(playerColor: PlayerColor): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`city_wall_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getRoadForPlayer(playerColor: PlayerColor): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`road_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getRoadHighlight(): PIXI.Texture {
        const resourceId: Sprites = Sprites[`road_highlight`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getShipForPlayer(playerColor: PlayerColor, edgeVariable: TileEdgeDirection): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`ship_${colorId}_${formatEdgeVariableAsString(edgeVariable)}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getShipHighlight(edgeVariable: TileEdgeDirection): PIXI.Texture {
        const resourceId: Sprites = Sprites[`ship_highlight_${formatEdgeVariableAsString(edgeVariable)}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getKnightForPlayer(playerColor: PlayerColor, knightType: TileCornerBuildingType): PIXI.Texture {
        const knightStatus = formatKnightAsString(knightType)
        if(knightStatus == undefined) {
            logError('getKnightForPlayer', [playerColor])
            return getTextureFromSpritesheet(Sprites.knight_level1_inactive_green)
        }
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`knight_${knightStatus}_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export function getMetropolisForCityImprovementType(cityImprovementType: CityImprovementTypes): PIXI.Texture {
        switch(cityImprovementType) {
            case CityImprovementTypes.Politics:
                return getTextureFromSpritesheet(Sprites.metropolis_politics)
            case CityImprovementTypes.Trade:
                return getTextureFromSpritesheet(Sprites.metropolis_trade)
            case CityImprovementTypes.Science:
                return getTextureFromSpritesheet(Sprites.metropolis_science)
        }
    }

    export function getMetropolisForCornerBonusType(cornerBonus: TileCornerBonusType): PIXI.Texture {
        switch(cornerBonus) {
            case TileCornerBonusType.MetropolisPolitics:
                return getTextureFromSpritesheet(Sprites.metropolis_politics)
            case TileCornerBonusType.MetropolisTrade:
                return getTextureFromSpritesheet(Sprites.metropolis_trade)
            case TileCornerBonusType.MetropolisScience:
                return getTextureFromSpritesheet(Sprites.metropolis_science)
            default:
                logError('getMetropolisForCornerBonusType', [])
                return getTextureFromSpritesheet(Sprites.metropolis_science)
        }
    }

    export function getEventDiceBackground(num: number): PIXI.Texture {
        /* tslint:disable */
        switch(num) {
            case 1:
            case 2:
            case 3:
                return getTextureFromSpritesheet(Sprites.dice)
            case EventDiceEnum.Science:
                return getTextureFromSpritesheet(Sprites.dice_background_science)
            case EventDiceEnum.Trade:
                return getTextureFromSpritesheet(Sprites.dice_background_trade)
            case EventDiceEnum.Politics:
                return getTextureFromSpritesheet(Sprites.dice_background_politics)
        }
        /* tslint:enable */
        logError('getEventDiceBackground', [num])
        return getTextureFromSpritesheet(Sprites.dice)
    }

    export function getEventDiceIcon(num: number): PIXI.Texture {
        /* tslint:disable */
        switch(num) {
            case 1:
                return getTextureFromSpritesheet(Sprites.icon_barbarian_ship)
            case 2:
                return getTextureFromSpritesheet(Sprites.icon_barbarian_ship)
            case 3:
                return getTextureFromSpritesheet(Sprites.icon_barbarian_ship)
            case EventDiceEnum.Science:
            case EventDiceEnum.Trade:
            case EventDiceEnum.Politics:
                return getTextureFromSpritesheet(Sprites.icon_gamemode_citiesandknights)
        }
        /* tslint:enable */
        logError('getEventDiceIcon', [num])
        return getTextureFromSpritesheet(Sprites.icon_barbarian_ship)
    }

    export function getButtonBadgeForPlayer(playerColor: PlayerColor): PIXI.Texture {
        const colorId = mapPlayerColorToColorId(playerColor)
        const resourceId: Sprites = Sprites[`button_badge_background_${colorId}`]
        return getTextureFromSpritesheet(resourceId)
    }

    export async function getPlayerIcon(playerState: PlayerValidator): Promise<{texture: PIXI.Texture, applyCircleMask: boolean}> {
        if(!playerState.isUser) return {texture: getTextureFromSpritesheet(Sprites.icon_bot), applyCircleMask: false}
        if(!playerState.isConnected && !uiGameManager.gameController.isReplay) return {texture: getTextureFromSpritesheet(Sprites.icon_bot), applyCircleMask: false}

        if(playerState.profilePictureUrl) {
            try {
                const texture = await AsyncAssetLoader.getAsyncTextureExternal(playerState.profilePictureUrl)
                return {texture: texture, applyCircleMask: true}
            } catch(err) {
                try {
                    const defaultDiscordAvatarUrl = 'https://cdn.discordapp.com/embed/avatars/0.png'
                    const texture = await AsyncAssetLoader.getAsyncTextureExternal(defaultDiscordAvatarUrl)
                    return {texture: texture, applyCircleMask: true}
                } catch(error) {
                    // continue to fallback texture
                }
            }
        }

        const imgFromSpriteSheet = getImageFromSpriteSheet(UIImagePathController.getImgPath(playerState.displayIcon))

        if(imgFromSpriteSheet != undefined) return {texture: imgFromSpriteSheet, applyCircleMask: false}

        const fallbackTexture = await AsyncAssetLoader.getAsyncTexture(UIImagePathController.getImgPath(playerState.displayIcon))
        return {texture: fallbackTexture, applyCircleMask: false}
    }

    export function getPlayerBg(playerColor: PlayerColor): PIXI.Texture {
        return getTextureFromSpritesheet(UIImagePathController.playerBackgroundByColor[playerColor])
    }

    export function getTextureFromSpritesheet(textureKeyOrImageURL: Sprites | ImageURL): PIXI.Texture {
        const textureKey = getAssetFileNameFromUrl(textureKeyOrImageURL)

        if(spritesheetMap == undefined) spritesheetMap = getSpriteToSpritesheetMap()
        const sheetName = spritesheetMap.get(textureKey)
        if(!sheetName) {
            // eslint-disable-next-line no-console
            console.error('getTextureFromSpritesheet, couldn\'t find sheet name for', [textureKey])
            return PIXI.Texture.WHITE
        }

        if(loadedAssets == undefined) return PIXI.Texture.WHITE

        const spritesheet = loadedAssets[sheetName] as PIXI.Spritesheet
        if(spritesheet == undefined) return PIXI.Texture.WHITE
        return spritesheet.textures[textureKey]
    }

    export function getTexture(textureKey: string): PIXI.Texture {
        if(loadedAssets == undefined) return PIXI.Texture.EMPTY
        const texture = loadedAssets[textureKey] as PIXI.Texture
        if(texture == undefined) return PIXI.Texture.EMPTY
        return texture
    }

    export function getSpritesheet(sheetName: string): PIXI.Spritesheet | undefined {
        if(loadedAssets == undefined) return undefined
        return loadedAssets[sheetName] as PIXI.Spritesheet
    }

    export function getTextureForLobbyImage(lobbyImage: LobbyImage): PIXI.Texture {
        const fileName = getAssetFileNameFromUrl(UIImagePathController.getImgPath(lobbyImage))
        const texture = getImageNameFromFileName(fileName)
        if(!texture) return PIXI.Texture.EMPTY

        if(loadedAssets == undefined) return PIXI.Texture.EMPTY

        const pixiTexture = loadedAssets[texture]
        if(pixiTexture == undefined) return PIXI.Texture.EMPTY
        return pixiTexture as PIXI.Texture
    }

    export function mapPlayerColorToColorId(playerColor: PlayerColor): Exclude<Lowercase<keyof typeof PlayerColor>, 'none'> {
        if(playerColor == PlayerColor.None) return 'white'
        return EnumUtils.getEnumKey(playerColor, PlayerColor).toLocaleLowerCase() as Exclude<Lowercase<keyof typeof PlayerColor>, 'none'>
    }

    export function getSpriteToSpritesheetMap(): Map<string, string> {
        const localSpritesheetMap: Map<string, string> = new Map()

        for(const key in loadedAssets) {
            if(!key.startsWith('game_spritesheet_')) continue
            const spriteSheet = loadedAssets[key] as PIXI.Spritesheet | undefined
            if(!spriteSheet) continue
            Object.keys(spriteSheet.textures).forEach((texture: string) => {
                localSpritesheetMap.set(texture, key)
            })
        }
        return localSpritesheetMap
    }

    export function getImageFromSpriteSheet(assetUrl: string): PIXI.Texture | undefined {
        if(spritesheetMap == undefined) return undefined
        if(!assetUrl.includes('.svg')) return undefined

        const assetName = getAssetFileNameFromUrl(assetUrl)
        if(assetName == undefined) return undefined

        if(!Object.values(Sprites).includes(assetName as Sprites)) return undefined
        return getTextureFromSpritesheet(assetName as Sprites)
    }
}

// handles name, name.svg, /path/name.svg, /path/name.svg?rev, /path/name.hash.svg
function getAssetFileNameFromUrl(url: string): string {
    const assetFilename = ArrayUtils.last(url.split('/'))
    const name = assetFilename.split('.')[0]
    return name
}

function formatEdgeVariableAsString(edgeVariable: TileEdgeDirection): 'north_west' | 'west' | 'south_west' {
    switch(edgeVariable) {
        case TileEdgeDirection.NorthWest:
            return 'north_west'
        case TileEdgeDirection.West:
            return 'west'
        case TileEdgeDirection.SouthWest:
            return 'south_west'
    }
}

type KnightString =
    'level1_active' |
    'level2_active' |
    'level3_active' |
    'level1_inactive' |
    'level2_inactive' |
    'level3_inactive'

function formatKnightAsString(knightType: TileCornerBuildingType): KnightString | undefined {
    switch(knightType) {
        case TileCornerBuildingType.KnightActiveLevel1:
            return 'level1_active'
        case TileCornerBuildingType.KnightActiveLevel2:
            return 'level2_active'
        case TileCornerBuildingType.KnightActiveLevel3:
            return 'level3_active'
        case TileCornerBuildingType.KnightInactiveLevel1:
            return 'level1_inactive'
        case TileCornerBuildingType.KnightInactiveLevel2:
            return 'level2_inactive'
        case TileCornerBuildingType.KnightInactiveLevel3:
            return 'level3_inactive'
        default:
            return undefined
    }
}
